import { type BaseSyntheticEvent, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import {
    type AttachClientContactInput,
    attachClientContactInputSchema,
    type ClientContactCandidate,
    clientPersonOptions,
    type EmailInUseError,
    isPersonAlreadyOnClientError,
    resolveEmailInUseError,
    useAttachClientContactOptions,
} from '@/entities/client';
import { useSheetStore } from '@/shared/stores/sheet-store';

import { ATTACH_CLIENT_CONTACT_DEFAULTS } from '../config/add-client-contact-defaults.config';
import { buildExistingPersonFields } from '../lib/build-existing-person-fields';
import {
    buildLockedPersonFields,
    type LockedPersonFields,
} from '../lib/build-locked-person-fields';
import { hasAddressContent } from '../lib/has-address-content';

const PERSON_ALREADY_ON_CLIENT_MESSAGE = 'This person is already a contact of this client';

export function useAttachClientContactForm(clientId: string) {
    const goBack = useSheetStore((state) => state.goBack);
    const queryClient = useQueryClient();

    const attachContactMutation = useMutation(useAttachClientContactOptions(clientId));

    const form = useForm<AttachClientContactInput>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        resolver: zodResolver(attachClientContactInputSchema, undefined, { raw: true }),
        defaultValues: ATTACH_CLIENT_CONTACT_DEFAULTS,
    });

    // Everything below outlives the Existing tab's body: Radix unmounts an inactive tab, and the
    // search text, the selection and the disclosure are not in the form store (research D10).
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPerson, setSelectedPerson] = useState<Omit<
        ClientContactCandidate,
        'alreadyContact'
    > | null>(null);
    const [lockedFields, setLockedFields] = useState<LockedPersonFields | null>(null);
    const [isAddressOpen, setIsAddressOpen] = useState(false);
    const [isLoadingPerson, setIsLoadingPerson] = useState(false);
    const [emailInUse, setEmailInUse] = useState<EmailInUseError | null>(null);

    const latestSelection = useRef(0);

    // Resolves to whether the person is now selected. Nothing changes on a failed load, so the
    // duplicate-email link can stay on the New contact tab (research D11).
    const selectPerson = async (personId: string): Promise<boolean> => {
        const selection = ++latestSelection.current;
        setIsLoadingPerson(true);

        try {
            // The person's details are editable from here, so a copy cached from an earlier
            // selection may be out of date.
            const person = await queryClient.fetchQuery({
                ...clientPersonOptions(personId),
                staleTime: 0,
            });
            // A quicker later selection owns the form; this one must not overwrite it.
            if (selection !== latestSelection.current) return false;

            // Only the person-owned fields are replaced, so linking the holder of a duplicate email
            // in place keeps the relationship, flags and notes as the therapist left them
            // (research D12).
            form.reset({
                ...form.getValues(),
                personId: person.id,
                ...buildExistingPersonFields(person),
            });
            setSelectedPerson({
                personId: person.id,
                firstName: person.firstName,
                lastName: person.lastName,
            });
            setLockedFields(buildLockedPersonFields(person));
            setSearchTerm('');
            setIsAddressOpen(hasAddressContent(person.address));
            setEmailInUse(null);
            return true;
        } catch (error) {
            console.error(error);
            if (selection === latestSelection.current) toast.error('Failed to load contact');
            return false;
        } finally {
            if (selection === latestSelection.current) setIsLoadingPerson(false);
        }
    };

    // Also cancels a selection still loading, so it cannot land on a tab that was started over.
    const reset = () => {
        latestSelection.current++;
        form.reset(ATTACH_CLIENT_CONTACT_DEFAULTS);
        setSearchTerm('');
        setSelectedPerson(null);
        setLockedFields(null);
        setIsAddressOpen(false);
        setIsLoadingPerson(false);
        setEmailInUse(null);
    };

    const onSubmit = async (data: AttachClientContactInput) => {
        const sheetAtSubmit = useSheetStore.getState().currentSheet;

        try {
            await attachContactMutation.mutateAsync(data);

            if (useSheetStore.getState().currentSheet === sheetAtSubmit) {
                goBack();
            }
        } catch (error) {
            // Either way the selection and every value stay (FR-027): neither reset nor navigate.
            const emailConflict = resolveEmailInUseError(error);
            if (emailConflict) {
                setEmailInUse(emailConflict);
            } else if (isPersonAlreadyOnClientError(error)) {
                toast.error(PERSON_ALREADY_ON_CLIENT_MESSAGE);
            }
        }
    };

    const handleSubmit = form.handleSubmit(onSubmit);

    return {
        form,
        // Enter bypasses the disabled Save button while a replacement person is still loading.
        submit: (event?: BaseSyntheticEvent) =>
            isLoadingPerson ? event?.preventDefault() : handleSubmit(event),
        isPending: attachContactMutation.isPending,
        searchTerm,
        setSearchTerm,
        selectedPerson,
        lockedFields,
        selectPerson,
        reset,
        isLoadingPerson,
        isAddressOpen,
        setIsAddressOpen,
        emailInUse,
        clearEmailInUse: () => setEmailInUse(null),
    };
}
